import { ReportCardGroup } from '@app/shared/constants';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { HelpersService } from '@app/shared/services/helpers.service';
import {
  ReportCardGroups
} from '@app/pages/authenticated/pages/reports/components/machool-report-cards/machool-report-cards.component';


interface CardGroup {
  key: string;
  label: string;
  cards: ReportCardGroup[];
  expanded?: boolean;
}

interface CardPreferences {
  [key: string]: boolean;
}

@Component({
  standalone: false,
  selector: 'app-card-selector',
  templateUrl: './card-selector.component.html',
  animations: [
    trigger('slideInOut', [
      state('in', style({
        height: '*',
        opacity: 1,
        transform: 'translateY(0)'
      })),
      state('out', style({
        height: '0',
        opacity: 0,
        transform: 'translateY(-20px)'
      })),
      transition('in => out', animate('300ms ease-in-out')),
      transition('out => in', animate('300ms ease-in-out'))
    ])
  ],
  styleUrls: ['./card-selector.component.scss']
})
export class CardSelectorComponent implements OnInit {
  @Input() initialSelectedCards: ReportCardGroup[] = [];
  @Input() allCurrentCards: ReportCardGroups[] = [];
  @Input() defaultCards: ReportCardGroup[] = [];
  @Input() displayQuickActions: boolean = false;
  @Input() showSelector: boolean = false;
  @Output() preferencesChanged = new EventEmitter<ReportCardGroup[]>();
  @Output() preferencesSaved = new EventEmitter<ReportCardGroup[]>();
  @Output() selectorToggled = new EventEmitter<boolean>();

  public selectedCards: CardPreferences = {};

  public cardGroups: CardGroup[] = [];

  constructor(private readonly helpersService: HelpersService) { }

  ngOnInit() {
    this.initializeCardGroups();
    this.initializeSelectedCards();
  }

  public toggleSelector() {
    this.showSelector = !this.showSelector;
    this.selectorToggled.emit(this.showSelector);
  }

  public getCardDisplayName(card: ReportCardGroup): string {
    // Convert enum values to human-readable names
    return this.helpersService.convertReportCardsToString(card);
  }

  public toggleCard(card: ReportCardGroup, event: MatCheckboxChange) {
    this.selectedCards[card] = event.checked;
    this.emitChanges();
  }

  public toggleGroup(group: CardGroup, event: MatCheckboxChange) {
    group.cards.forEach(card => {
      this.selectedCards[card] = event.checked;
    });
    this.emitChanges();
  }

  public isGroupFullySelected(group: CardGroup): boolean {
    return group.cards.every(card => this.selectedCards[card]);
  }

  public isGroupPartiallySelected(group: CardGroup): boolean {
    const selectedCount = group.cards.filter(card => this.selectedCards[card]).length;
    return selectedCount > 0 && selectedCount < group.cards.length;
  }

  public getSelectedCount(group: CardGroup): number {
    return group.cards.filter(card => this.selectedCards[card]).length;
  }

  public selectAll() {
    Object.keys(this.selectedCards).forEach(card => {
      this.selectedCards[card] = true;
    });
    this.emitChanges();
  }

  public deselectAll() {
    Object.keys(this.selectedCards).forEach(card => {
      this.selectedCards[card] = false;
    });
    this.emitChanges();
  }

  public resetToDefaults() {
    // Reset all to false first
    Object.keys(this.selectedCards).forEach(card => {
      this.selectedCards[card] = false;
    });

    // Set defaults
    this.defaultCards.forEach(card => {
      this.selectedCards[card] = true;
    });

    this.emitChanges();
  }

  public savePreferences() {
    const selected = this.getSelectedCards();
    this.preferencesSaved.emit(selected);
    this.toggleSelector();
  }

  private emitChanges() {
    const selected = this.getSelectedCards();
    this.preferencesChanged.emit(selected);
  }

  private initializeCardGroups(): void {
    this.cardGroups = [];
    this.allCurrentCards.forEach((group) => {
      this.cardGroups.push({
        key: group.id,
        label: group.label,
        cards: group.cards.map((card) => card.type),
        expanded: false,
      })
    });
  }

  private initializeSelectedCards() {
    // Initialize all cards as unselected
    this.cardGroups.forEach(group => {
      group.cards.forEach(card => {
        this.selectedCards[card] = false;
      });
    });

    // Set initially selected cards
    this.initialSelectedCards.forEach(card => {
      this.selectedCards[card] = true;
    });
  }

  private getSelectedCards(): ReportCardGroup[] {
    return Object.keys(this.selectedCards)
      .filter(card => this.selectedCards[card])
      .map(card => card as ReportCardGroup);
  }
}
